import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlayIcon, CodeIcon, LayoutIcon, SparklesIcon, BrainIcon, ExternalLinkIcon, XIcon } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useWindows } from '../../contexts/WindowContext';
import { getGradientClasses } from '../../utils/helpers';
type Project = {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  tags: string[];
  type: 'video' | 'ui' | 'code' | 'ai';
  color: string;
  rotation: number;
  image?: string;
  thumbnail?: string;
  videoUrl?: string;
  codeSnippet?: string;
  link?: string;
};
export function PortfolioApp() {
  const {
    getThemeClasses,
    theme
  } = useTheme();
  const {
    isMobile
  } = useWindows();
  const themeClasses = getThemeClasses();
  const gradients = getGradientClasses(theme);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [hoveredProject, setHoveredProject] = useState<string | null>(null);
  const projects: Project[] = [{
    id: 'bulkrr',
    title: 'BULKRR',
    subtitle: 'AI Shopping Assistant',
    description: 'Een AI-gedreven boodschappenapp die gebruikers helpt slimmer in te kopen op basis van prijs, voorraad, aanbiedingen en verbruik.',
    tags: ['AI', 'UX', 'App Development'],
    type: 'ui',
    color: 'from-emerald-500 to-teal-600',
    rotation: -2,
    image: 'https://images.pexels.com/photos/5632402/pexels-photo-5632402.jpeg?auto=compress&cs=tinysrgb&w=800'
  }, {
    id: 'olympia',
    title: 'Olympia Camping',
    subtitle: 'Group Experience Platform',
    description: 'Een gebruiksvriendelijke webapp voor gasten en beheerders van campings, met functies voor informatie, communicatie en praktische services.',
    tags: ['UX', 'Web App', 'Design Systems'],
    type: 'ui',
    color: 'from-sky-500 to-blue-600',
    rotation: 3,
    image: 'https://images.pexels.com/photos/1687845/pexels-photo-1687845.jpeg?auto=compress&cs=tinysrgb&w=800'
  }, {
    id: 'ai-characters',
    title: 'AI Characters',
    subtitle: 'Digital Storytelling',
    description: 'Een reeks digitale karakters met unieke persoonlijkheden, emoties en verhaallijnen. Gecreëerd met AI-tools zoals Kling AI en Flux Pro Ultra.',
    tags: ['AI', 'Animation', 'Character Design'],
    type: 'video',
    color: 'from-rose-500 to-pink-600',
    rotation: -3,
    thumbnail: 'https://images.pexels.com/photos/8728380/pexels-photo-8728380.jpeg?auto=compress&cs=tinysrgb&w=800',
    videoUrl: 'https://player.vimeo.com/progressive_redirect/playback/855551359/rendition/720p/file.mp4?loc=external&signature=f33dba70e7d6d3d58a1ef34b6f569594418b276e8e7bd077e72e182d634aec6e'
  }, {
    id: 'dashboard',
    title: 'Interactive Dashboard',
    subtitle: 'React + AI Integration',
    description: 'Een dashboard dat complexe data automatisch structureert en presenteert op basis van gebruikerscontext.',
    tags: ['React', 'AI', 'Data Viz'],
    type: 'ui',
    color: 'from-blue-500 to-indigo-600',
    rotation: 1,
    image: 'https://images.pexels.com/photos/669615/pexels-photo-669615.jpeg?auto=compress&cs=tinysrgb&w=800'
  }, {
    id: 'prompt-engineering',
    title: 'Prompt Engineering',
    subtitle: 'AI Character System',
    description: 'Een systematische aanpak voor het creëren van consistente AI-karakters met persoonlijkheid en emotionele diepte.',
    tags: ['AI', 'Prompt Engineering', 'Systems'],
    type: 'code',
    color: 'from-purple-500 to-violet-600',
    rotation: -1,
    codeSnippet: `// AI Character System
const createCharacter = (traits) => {
  return {
    personality: traits.personality,
    visual: traits.visual,
    emotion: traits.emotion,
    context: traits.context
  }
}
// Example usage
const character = createCharacter({
  personality: "Curious explorer",
  visual: "Cyberpunk aesthetic",
  emotion: "Wonder and excitement",
  context: "Futuristic cityscape"
})`
  }, {
    id: 'rijsslim',
    title: 'RijsSlim',
    subtitle: 'Driving Education App',
    description: 'Een educatief app-concept dat rijvaardigheden en verkeersveiligheid verbetert met AI-feedback en voortgangsvisualisatie.',
    tags: ['UX', 'Education', 'Mobile'],
    type: 'ui',
    color: 'from-amber-500 to-orange-600',
    rotation: 2,
    image: 'https://images.pexels.com/photos/1545743/pexels-photo-1545743.jpeg?auto=compress&cs=tinysrgb&w=800'
  }];
  // Desktop positions
  const desktopPositions = [{
    top: '0%',
    left: '5%'
  }, {
    top: '15%',
    left: '55%'
  }, {
    top: '35%',
    left: '10%'
  }, {
    top: '55%',
    left: '50%'
  }, {
    top: '75%',
    left: '15%'
  }, {
    top: '95%',
    left: '55%'
  }];
  return <div className={`p-4 md:p-6 lg:p-8 ${themeClasses.text} h-full overflow-y-auto`} data-id="element-251">
      {/* Header */}
      <motion.div initial={{
      opacity: 0,
      y: -20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="mb-6 md:mb-8" data-id="element-252">
        <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-2 md:mb-3" data-id="element-253">
          Portfolio
        </h1>
        <p className={`text-base md:text-lg ${themeClasses.text} opacity-70 max-w-3xl`} data-id="element-254">
          Projecten waarin ik AI, design en UX combineer om ideeën tot leven te
          brengen
        </p>
      </motion.div>
      {/* Mobile: Vertical Stack Layout */}
      {isMobile && <div className="space-y-4" data-id="element-255">
          {projects.map((project, index) => <motion.div key={project.id} initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.1 + index * 0.1
      }} onClick={() => setSelectedProject(project)} className="relative" data-id="element-256">
              <div className={`relative h-56 rounded-2xl overflow-hidden ${themeClasses.card} border-2 ${themeClasses.border} backdrop-blur-xl`} style={{
          boxShadow: '0 10px 30px -10px rgba(0, 0, 0, 0.2)',
          transform: `rotate(${project.rotation}deg)`
        }} data-id="element-257">
                {/* Background gradient */}
                <div className={`absolute inset-0 bg-gradient-to-br ${project.color} opacity-20`} data-id="element-258" />
                {/* Content based on type */}
                {project.type === 'video' && project.thumbnail && <>
                    <img src={project.thumbnail} alt={project.title} className="w-full h-full object-cover" data-id="element-259" />
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center" data-id="element-260">
                      <div className={`w-14 h-14 rounded-full bg-gradient-to-br ${project.color} flex items-center justify-center`} data-id="element-261">
                        <PlayIcon className="w-7 h-7 text-white ml-1" data-id="element-262" />
                      </div>
                    </div>
                  </>}
                {project.type === 'ui' && project.image && <>
                    <img src={project.image} alt={project.title} className="w-full h-full object-cover" data-id="element-263" />
                    <div className={`absolute inset-0 bg-gradient-to-br ${project.color} opacity-30 mix-blend-multiply`} data-id="element-264" />
                  </>}
                {project.type === 'code' && project.codeSnippet && <div className="w-full h-full p-5 flex flex-col justify-center" data-id="element-265">
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${project.color} flex items-center justify-center mb-3`} data-id="element-266">
                      <CodeIcon className="w-5 h-5 text-white" data-id="element-267" />
                    </div>
                    <pre className={`text-xs ${themeClasses.text} opacity-70 font-mono overflow-hidden line-clamp-6`} data-id="element-268">
                      {project.codeSnippet}
                    </pre>
                  </div>}
                {/* Label overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent" data-id="element-269">
                  <div className="flex items-center gap-2 mb-1" data-id="element-270">
                    {project.type === 'video' && <PlayIcon className="w-4 h-4 text-white" data-id="element-271" />}
                    {project.type === 'ui' && <LayoutIcon className="w-4 h-4 text-white" data-id="element-272" />}
                    {project.type === 'code' && <CodeIcon className="w-4 h-4 text-white" data-id="element-273" />}
                    {project.type === 'ai' && <BrainIcon className="w-4 h-4 text-white" data-id="element-274" />}
                    <h3 className="text-white font-bold text-sm" data-id="element-275">
                      {project.title}
                    </h3>
                  </div>
                  <p className="text-white/70 text-xs" data-id="element-276">{project.subtitle}</p>
                </div>
              </div>
              {/* Pin effect */}
              <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-5 h-5 rounded-full bg-gradient-to-br from-red-400 to-red-600 shadow-lg z-10" data-id="element-277" />
            </motion.div>)}
        </div>}
      {/* Desktop: Pinboard Style Project Grid */}
      {!isMobile && <div className="relative" style={{
      minHeight: '1200px'
    }} data-id="element-278">
          {projects.map((project, index) => <motion.div key={project.id} initial={{
        opacity: 0,
        scale: 0.8,
        rotate: 0
      }} animate={{
        opacity: 1,
        scale: hoveredProject === project.id ? 1.05 : 1,
        rotate: hoveredProject === project.id ? 0 : project.rotation
      }} transition={{
        delay: 0.1 + index * 0.1,
        type: 'spring',
        stiffness: 260,
        damping: 20
      }} onHoverStart={() => setHoveredProject(project.id)} onHoverEnd={() => setHoveredProject(null)} onClick={() => setSelectedProject(project)} className="absolute w-full max-w-sm cursor-pointer" style={{
        top: desktopPositions[index]?.top || '0%',
        left: desktopPositions[index]?.left || '0%',
        zIndex: hoveredProject === project.id ? 50 : 10 + index
      }} data-id="element-279">
              <div className={`relative h-64 rounded-2xl overflow-hidden ${themeClasses.card} border-2 ${themeClasses.border} backdrop-blur-xl`} style={{
          boxShadow: hoveredProject === project.id ? '0 25px 50px -12px rgba(0, 0, 0, 0.35)' : '0 10px 30px -10px rgba(0, 0, 0, 0.2)'
        }} data-id="element-280">
                {/* Background gradient */}
                <div className={`absolute inset-0 bg-gradient-to-br ${project.color} opacity-20`} data-id="element-281" />
                {/* Content based on type */}
                {project.type === 'video' && project.thumbnail && <>
                    <img src={project.thumbnail} alt={project.title} className="w-full h-full object-cover" data-id="element-282" />
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center" data-id="element-283">
                      <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${project.color} flex items-center justify-center`} data-id="element-284">
                        <PlayIcon className="w-8 h-8 text-white ml-1" data-id="element-285" />
                      </div>
                    </div>
                  </>}
                {project.type === 'ui' && project.image && <>
                    <img src={project.image} alt={project.title} className="w-full h-full object-cover" data-id="element-286" />
                    <div className={`absolute inset-0 bg-gradient-to-br ${project.color} opacity-30 mix-blend-multiply`} data-id="element-287" />
                  </>}
                {project.type === 'code' && project.codeSnippet && <div className="w-full h-full p-6 flex flex-col justify-center" data-id="element-288">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${project.color} flex items-center justify-center mb-4`} data-id="element-289">
                      <CodeIcon className="w-6 h-6 text-white" data-id="element-290" />
                    </div>
                    <pre className={`text-xs ${themeClasses.text} opacity-70 font-mono overflow-hidden line-clamp-6`} data-id="element-291">
                      {project.codeSnippet}
                    </pre>
                  </div>}
                {/* Label overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent" data-id="element-292">
                  <div className="flex items-center gap-2 mb-1" data-id="element-293">
                    {project.type === 'video' && <PlayIcon className="w-4 h-4 text-white" data-id="element-294" />}
                    {project.type === 'ui' && <LayoutIcon className="w-4 h-4 text-white" data-id="element-295" />}
                    {project.type === 'code' && <CodeIcon className="w-4 h-4 text-white" data-id="element-296" />}
                    {project.type === 'ai' && <BrainIcon className="w-4 h-4 text-white" data-id="element-297" />}
                    <h3 className="text-white font-bold text-sm" data-id="element-298">
                      {project.title}
                    </h3>
                  </div>
                  <p className="text-white/70 text-xs" data-id="element-299">{project.subtitle}</p>
                </div>
                {/* Hover indicator */}
                {hoveredProject === project.id && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} className="absolute inset-0 border-4 border-white/20 rounded-2xl pointer-events-none" data-id="element-300" />}
              </div>
              {/* Pin effect */}
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-gradient-to-br from-red-400 to-red-600 shadow-lg" data-id="element-301" />
            </motion.div>)}
        </div>}
      {/* Project Detail Modal */}
      <AnimatePresence data-id="element-302">
        {selectedProject && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelectedProject(null)} data-id="element-303">
            <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.9,
          opacity: 0
        }} onClick={e => e.stopPropagation()} className={`relative w-full max-w-3xl max-h-[85vh] rounded-2xl ${themeClasses.card} border-2 ${themeClasses.border} flex flex-col`} data-id="element-304">
              {/* Fixed Header with Close Button */}
              <div className={`relative p-6 md:p-8 bg-gradient-to-br ${selectedProject.color} text-white rounded-t-2xl flex-shrink-0`} data-id="element-305">
                <button onClick={() => setSelectedProject(null)} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-white/30 transition-colors z-10" data-id="element-306">
                  <XIcon className="w-5 h-5" data-id="element-307" />
                </button>
                <div className="flex items-center gap-4 mb-4" data-id="element-308">
                  <div className="w-14 h-14 md:w-16 md:h-16 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center" data-id="element-309">
                    {selectedProject.type === 'video' && <PlayIcon className="w-7 h-7 md:w-8 md:h-8" data-id="element-310" />}
                    {selectedProject.type === 'ui' && <LayoutIcon className="w-7 h-7 md:w-8 md:h-8" data-id="element-311" />}
                    {selectedProject.type === 'code' && <CodeIcon className="w-7 h-7 md:w-8 md:h-8" data-id="element-312" />}
                    {selectedProject.type === 'ai' && <BrainIcon className="w-7 h-7 md:w-8 md:h-8" data-id="element-313" />}
                  </div>
                  <div data-id="element-314">
                    <h2 className="text-2xl md:text-3xl font-bold" data-id="element-315">
                      {selectedProject.title}
                    </h2>
                    <p className="text-white/80 text-sm md:text-base" data-id="element-316">
                      {selectedProject.subtitle}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2" data-id="element-317">
                  {selectedProject.tags.map(tag => <span key={tag} className="px-3 py-1 rounded-lg bg-white/20 backdrop-blur-sm text-white text-xs md:text-sm" data-id="element-318">
                      {tag}
                    </span>)}
                </div>
              </div>
              {/* Scrollable Content */}
              <div className="overflow-y-auto custom-scrollbar flex-1" data-id="element-319">
                <div className="p-6 md:p-8" data-id="element-320">
                  <p className={`text-base md:text-lg ${themeClasses.text} opacity-80 leading-relaxed mb-6`} data-id="element-321">
                    {selectedProject.description}
                  </p>
                  {/* Media */}
                  {selectedProject.type === 'video' && selectedProject.videoUrl && <div className="aspect-video rounded-xl overflow-hidden mb-6" data-id="element-322">
                        <video src={selectedProject.videoUrl} controls className="w-full h-full" data-id="element-323" />
                      </div>}
                  {selectedProject.type === 'ui' && selectedProject.image && <div className="rounded-xl overflow-hidden mb-6" data-id="element-324">
                      <img src={selectedProject.image} alt={selectedProject.title} className="w-full" data-id="element-325" />
                    </div>}
                  {selectedProject.type === 'code' && selectedProject.codeSnippet && <div className={`p-4 rounded-xl ${themeClasses.card} border ${themeClasses.border} mb-6`} data-id="element-326">
                        <pre className={`text-xs md:text-sm ${themeClasses.text} font-mono overflow-x-auto`} data-id="element-327">
                          {selectedProject.codeSnippet}
                        </pre>
                      </div>}
                  {selectedProject.link && <a href={selectedProject.link} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-br ${selectedProject.color} text-white font-semibold hover:scale-105 transition-all text-sm md:text-base`} data-id="element-328">
                      <span data-id="element-329">Bekijk Project</span>
                      <ExternalLinkIcon className="w-4 h-4" data-id="element-330" />
                    </a>}
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}